/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useEffect, useState } from 'react';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { addSentryBreadcrumb } from '../../shared/sentry/sentryUser';
import type { TrialUrgencyPhase } from '../../../../../shared/billing-catalog';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';

type PosTrialBannerProps = {
  planName: string;
  subtitle: string;
  urgency?: TrialUrgencyPhase;
  ctaLabel?: string;
  onPressCta: () => void;
};

const DISMISS_KEY = 'pos_trial_banner_dismissed';

export const PosTrialBanner = ({
  planName,
  subtitle,
  urgency = 'normal',
  ctaLabel = 'Suscribirme',
  onPressCta,
}: PosTrialBannerProps) => {
  const [dismissed, setDismissed] = useState(false);
  const urgent = urgency === 'last_day' || urgency === 'soon';

  useEffect(() => {
    AsyncStorage.getItem(DISMISS_KEY).then((v) => {
      if (v === '1') setDismissed(true);
    }).catch((error) => {
      addSentryBreadcrumb('pos', 'trial-banner AsyncStorage.getItem failed', 'warning', { error: String(error) });
    });
  }, []);

  if (dismissed) return null;

  return (
    <View
      style={[
        styles.banner,
        urgency === 'soon' && styles.bannerSoon,
        urgency === 'last_day' && styles.bannerEnding,
      ]}
    >
      <View style={styles.iconWrap}>
        <Ionicons name="sparkles" size={16} color={ticketColors.gold} />
      </View>
      <View style={styles.texts}>
        <Text style={styles.title}>
          Probás plan <Text style={styles.planName}>{planName}</Text> gratis
        </Text>
        <Text style={[styles.subtitle, urgent && styles.subtitleUrgent]}>
          {subtitle}
        </Text>
      </View>
      <TouchableOpacity
        style={styles.cta}
        onPress={onPressCta}
        accessibilityRole="button"
        accessibilityLabel={`${ctaLabel}. ${subtitle}.`}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        <Text style={styles.ctaText}>{ctaLabel}</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.closeBtn}
        onPress={() => {
          setDismissed(true);
          AsyncStorage.setItem(DISMISS_KEY, '1').catch((error) => {
            addSentryBreadcrumb('pos', 'trial-banner AsyncStorage.setItem failed', 'warning', { error: String(error) });
          });
        }}
        accessibilityRole="button"
        accessibilityLabel="Cerrar banner de prueba"
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        <Ionicons name="close" size={16} color={ticketColors.ink3} />
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 0,
    borderWidth: 0,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
    backgroundColor: ticketColors.brandSoft,
    marginBottom: 0,
  },
  bannerSoon: {
    backgroundColor: ticketColors.brandSoft,
  },
  bannerEnding: {
    backgroundColor: ticketColors.brandSoft,
  },
  iconWrap: {
    width: 30,
    height: 30,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.paper,
  },
  texts: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    color: ticketColors.ink,
    fontSize: 14,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  planName: {
    color: ticketColors.gold,
    fontWeight: '800',
  },
  subtitle: {
    color: ticketColors.ink2,
    fontSize: 12,
    marginTop: 1,
    fontFamily: ticketFonts.sans,
  },
  subtitleUrgent: {
    color: ticketColors.gold,
    fontWeight: '600',
  },
  cta: {
    flexShrink: 0,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
  },
  ctaText: {
    color: ticketColors.goldInk,
    fontSize: 13,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  closeBtn: {
    flexShrink: 0,
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
